import {
  AttributeValue,
  Product,
  ProductType,
  ProductVariable,
  Promotion,
  Quotation,
  useBulkQuotation,
  useVariant,
} from '@bowtie-web/common';
import { BulkQuotation } from '@bowtie-web/common/lib';
import { compact, findIndex, inRange } from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMap } from 'react-use';
import { EXTRA_PRODUCT_SPECS_BY_CODE } from '../constants/product';
import {
  ProductWithSocialProof,
  SOCIAL_PROOF_BY_PRODUCT,
} from '../constants/socialProof';
import {
  TERM_LIFE_YEAR_TO_PRODUCT_CODE,
  TermLifeYear,
  toSafeTermLifeYear,
} from '../constants/termLife';
import {
  getElementFromGroupByVariableId,
  getProductListAttributes,
  parseObjectValuesToNumber,
} from '../helpers';
import { useListProductGroup, useQuotationFormValues } from '../hooks';
import useSortProducts from '../hooks/useSortProducts';
import { getSafeProductType } from '../services/productMeta';
import { ProductSocialProofSpec, QuotationForm } from '../types';
import { useAttributeAndQsContext } from './AttributeAndQsContext';

export type QuotationContextValue = {
  values: QuotationForm;
  handleChange: (newFormValues: Record<string, string | number>) => void;
  coupon: Promotion | undefined;
  setCoupon: (promotion: Promotion | undefined) => void;
  isIssueAgeValid: boolean;
  isSumAssuredValid: boolean;
  setIsSumAssuredValid: (arg1: boolean) => void;
  productType: string;
  activeProduct: Product | null;
  setActiveProduct: (arg1: Product) => void;
  selectedVariable: AttributeValue | null;
  setSelectedVariable: (arg1: AttributeValue) => void;
  rawProducts: Product[];
  variables: ProductVariable[];
  products: Product[];
  bulkQuotation: BulkQuotation[];
  activeProductQuotation: Quotation | null | undefined;
  socialProof: ProductSocialProofSpec | undefined;
  enableShowingAnnualLevelTermLife: boolean | undefined;
  termLifeYear: TermLifeYear;
  setTermLifeYear: (year: TermLifeYear) => void;
};

export const QuotationContext: React.Context<QuotationContextValue> =
  createContext({} as QuotationContextValue);

export const useQuotationContext = () => {
  return useContext(QuotationContext);
};

export const QuotationProvider: React.FC = ({ children }) => {
  const {
    type,
    productsShow,
    variableAttribute: qsVariableAttribute = '',
    termLifeYear: qsTermLifeYear,
    ...qs
  } = useAttributeAndQsContext();

  const productType = getSafeProductType(type || '');
  // Seeded once from the qs value (which may come from a frozen parent-iframe
  // querystring snapshot); subsequent changes go through setTermLifeYear only,
  // so the slider keeps working after the qs merge stops reflecting it.
  const [termLifeYear, setTermLifeYear] = useState<TermLifeYear>(() =>
    toSafeTermLifeYear(qsTermLifeYear),
  );
  const [coupon, setCoupon] = React.useState<Promotion>();

  const [
    variableAttributeActiveProductMapping,
    { set: setVariableAttributeActiveProductMapping },
  ] = useMap<Record<string, Product | null>>({});

  const activeProduct = useMemo(() => {
    return variableAttributeActiveProductMapping[qsVariableAttribute] ?? null;
  }, [variableAttributeActiveProductMapping, qsVariableAttribute]);

  const setActiveProduct = useCallback(
    (product: Product | null) => {
      setVariableAttributeActiveProductMapping(qsVariableAttribute, product);
    },
    [setVariableAttributeActiveProductMapping, qsVariableAttribute],
  );

  const [isSumAssuredValid, setIsSumAssuredValid] = useState(true);
  const [selectedVariable, setSelectedVariable] =
    useState<AttributeValue | null>(null);

  const [formValues, setFormValues] = useQuotationFormValues();

  const handleChange = useCallback(
    (newFormValues: Record<string, string | number>) => {
      if (Object.keys(newFormValues).length > 0) {
        const newValues: Partial<QuotationForm> =
          parseObjectValuesToNumber(newFormValues);

        setFormValues({ ...formValues, ...newValues });
      }
    },
    [formValues, setFormValues],
  );

  const { value: enableShowingAnnualLevelTermLife } = useVariant(
    'enable_showing_annual_level_term_life',
  );

  const [productGroups] = useListProductGroup({
    type: productType,
    variableAttribute: qsVariableAttribute,
  });

  const variables = useMemo(() => {
    return productGroups ? getProductListAttributes(productGroups) : undefined;
  }, [productGroups]);

  const rawProducts = useMemo(() => {
    if (!productGroups) {
      return [];
    }
    const singleGroupProducts = productGroups[0].elements.map(
      element => element.resource,
    );

    const multiGroupProducts = compact(
      productGroups.map(group => {
        // WARNING: This is heuristic. Should consider when multiple variables apply.
        if (!selectedVariable) return undefined;
        return getElementFromGroupByVariableId(
          group.elements,
          selectedVariable.id,
        );
      }),
    );

    const products =
      productGroups.length === 1 ? singleGroupProducts : multiGroupProducts;

    // productsShow (below) indexes into this array by position, so it must be
    // deterministic regardless of the order the API happens to return elements in.
    return [...products].sort(
      (a, b) => (a.ordering ?? Infinity) - (b.ordering ?? Infinity),
    );
  }, [productGroups, selectedVariable]);

  const displayProducts = useMemo(() => {
    const productsShowIndex =
      productsShow
        ?.split(',')
        .filter(ix => !isNaN(Number(ix)))
        .map(ix => Number(ix))
        .sort((a, b) => a - b) || [];

    const products =
      productsShowIndex.length && !!rawProducts.length
        ? productsShowIndex.map(ix => rawProducts[ix])
        : rawProducts;

    if (productType === ProductType.LIFE) {
      if (!enableShowingAnnualLevelTermLife) {
        return products.filter(product => product.code === 'term-life');
      }
      const targetCode = TERM_LIFE_YEAR_TO_PRODUCT_CODE[termLifeYear];
      const filtered = products.filter(product => product.code === targetCode);
      // Fall back to term-life when the targeted variant product isn't in rawProducts
      // (backend not yet deployed, partial rollout). Without this the UI silently bricks.
      return filtered.length > 0
        ? filtered
        : products.filter(product => product.code === 'term-life');
    }

    return products;
  }, [
    productsShow,
    rawProducts,
    productType,
    enableShowingAnnualLevelTermLife,
    termLifeYear,
  ]);

  const sortedPromoteProducts = useSortProducts(displayProducts || []);

  const isIssueAgeValid = useMemo(() => {
    const {
      min_issue_age_value: minIssueAge,
      min_issue_age_unit: minIssueAgeUnit,
      max_issue_age_value: maxIssueAge,
    } = activeProduct || displayProducts[0] || {};

    const isIssueAgeValid =
      (minIssueAgeUnit === 'days' ? 0 : minIssueAge) <= formValues.age &&
      formValues.age <= maxIssueAge;

    return isIssueAgeValid;
  }, [activeProduct, displayProducts, formValues]);

  const [bulkQuotation] = useBulkQuotation({
    products: displayProducts.map(product => ({
      code: product.code,
      sum_assured: EXTRA_PRODUCT_SPECS_BY_CODE[product.code]?.sumAssured
        ? formValues.sumAssured
        : 0,
    })),
    age: formValues.age,
    gender: formValues.gender,
    smoker: formValues.smoker,
    coupon_code: coupon?.promo_code,
  });

  const activeProductQuotation = useMemo(() => {
    return bulkQuotation?.find(
      quotation => quotation.product.code === activeProduct?.code,
    )?.quotation;
  }, [activeProduct, bulkQuotation]);

  useEffect(() => {
    if (variables && !selectedVariable) {
      const variable = variables.find(
        v => v.attribute.key === qsVariableAttribute,
      );
      // TODO: allow users to customize default value
      setSelectedVariable(
        variable ? variable.values[qs.defaultVariable ?? 0] : null,
      );
    }
  }, [variables, qsVariableAttribute, qs.defaultVariable, selectedVariable]);

  // TODO: split into grouped vs not-grouped modes and separate logic accordingly
  useEffect(() => {
    if (!!productGroups && !!displayProducts) {
      const activeProductIsInDisplayProducts = displayProducts.some(
        ({ code }) => code === activeProduct?.code,
      );

      if (!activeProduct || !activeProductIsInDisplayProducts) {
        const coerced = Number(qs.defaultOption);
        const ix =
          Number.isNaN(coerced) || coerced + 1 > displayProducts.length
            ? 0
            : coerced;

        if (displayProducts[ix]) {
          setActiveProduct(displayProducts[ix]);
        }
      }

      if (productGroups.length > 1 && selectedVariable && activeProduct) {
        const groupIx = findIndex(productGroups, {
          elements: [{ resource: { code: activeProduct.code } }],
        });

        const group = productGroups[groupIx];

        if (group) {
          setActiveProduct(
            getElementFromGroupByVariableId(
              group.elements,
              selectedVariable.id,
            ) || null,
          );
        }
      }
    }
  }, [
    activeProduct,
    productGroups,
    qs.defaultOption,
    selectedVariable,
    displayProducts,
    setActiveProduct,
  ]);

  const productWithSocialProof = useMemo(() => {
    switch (productType) {
      case 'critical-illness':
        return ProductWithSocialProof.TERM_CI_MAX;
      case 'medical':
        return ProductWithSocialProof.CANCER_FIGHTER_300;
      case 'vhis':
      default:
        return qsVariableAttribute
          ? ProductWithSocialProof.VHIS_FLEXI_PREMIUM_SEMI_PRIVATE
          : ProductWithSocialProof.VHIS_FLEXI_REGULAR;
    }
  }, [productType, qsVariableAttribute]);

  const socialProof = useMemo(() => {
    if (qs.showSocialProofMessage !== 'true') return undefined;

    const isContainedProductWithSocialProof = displayProducts.some(product =>
      product.code.includes(productWithSocialProof),
    );

    const displaySocialProof = SOCIAL_PROOF_BY_PRODUCT[
      productWithSocialProof
    ].find(({ min_age, max_age }) =>
      inRange(formValues.age, min_age, max_age + 1),
    );

    return isContainedProductWithSocialProof && displaySocialProof
      ? {
          product_code: productWithSocialProof,
          ...displaySocialProof,
        }
      : undefined;
  }, [
    qs.showSocialProofMessage,
    displayProducts,
    formValues.age,
    productWithSocialProof,
  ]);

  const value = useMemo(
    () => ({
      values: formValues,
      handleChange,
      coupon,
      setCoupon,
      isIssueAgeValid,
      isSumAssuredValid,
      setIsSumAssuredValid,
      productType,
      activeProduct,
      setActiveProduct,
      selectedVariable,
      setSelectedVariable,
      rawProducts,
      variables: variables || [],
      products: sortedPromoteProducts || [],
      bulkQuotation,
      activeProductQuotation,
      socialProof,
      enableShowingAnnualLevelTermLife,
      termLifeYear,
      setTermLifeYear,
    }),
    [
      formValues,
      handleChange,
      coupon,
      isIssueAgeValid,
      isSumAssuredValid,
      productType,
      activeProduct,
      setActiveProduct,
      selectedVariable,
      rawProducts,
      variables,
      sortedPromoteProducts,
      bulkQuotation,
      activeProductQuotation,
      socialProof,
      enableShowingAnnualLevelTermLife,
      termLifeYear,
    ],
  );

  return (
    <QuotationContext.Provider value={value}>
      {children}
    </QuotationContext.Provider>
  );
};
